import React, { useState, useEffect, useRef } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { useSearchUsersQuery } from "../api";

export interface User {
    id: number;
    name: string;
    surname: string;
    email: string;
    mobile: string | null;
    secret_cookie: string | null;
    ref_link: string | null;
}

interface UserAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onUserSelect?: (user: User) => void;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

export const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
    value,
    onChange,
    onUserSelect,
    onBlur,
    onKeyDown,
    placeholder = "ID, email, phone, name...",
    className = "",
    disabled = false,
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [wasSelected, setWasSelected] = useState(false);
    const debouncedValue = useDebounce(value, 300);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Используем RTK Query для поиска
    const { data, isLoading, isFetching } = useSearchUsersQuery(
        { search: debouncedValue, limit: 10 },
        {
            skip: !debouncedValue || debouncedValue.length < 2,
        }
    );

    const suggestions = data?.data || [];

    // Сбрасываем флаг выбора при изменении значения пользователем
    useEffect(() => {
        // Если значение изменилось и это не просто ID (пользователь печатает)
        if (value && !/^\d+$/.test(value)) {
            setWasSelected(false);
        }
    }, [value]);

    // Показываем dropdown когда есть данные, но не после выбора
    useEffect(() => {
        if (debouncedValue && debouncedValue.length >= 2 && !wasSelected) {
            setShowDropdown(true);
        } else if (wasSelected || debouncedValue.length < 2) {
            setShowDropdown(false);
        }
    }, [debouncedValue, data, wasSelected]);

    // Закрытие при клике вне
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUserClick = (user: User) => {
        setWasSelected(true);
        setShowDropdown(false);
        onChange(user.id.toString());
        onUserSelect?.(user);
    };

    const formatUserDisplay = (user: User): string => {
        const parts = [`#${user.id}`, user.name, user.surname].filter(Boolean);

        if (user.email) parts.push(`(${user.email})`);
        else if (user.mobile) parts.push(`(${user.mobile})`);

        return parts.join(" ");
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                type="text"
                value={value}
                onChange={(e) => {
                    if (!disabled) {
                        setWasSelected(false);
                        onChange(e.target.value);
                    }
                }}
                onFocus={() => {
                    if (!disabled) {
                        // Показываем dropdown при фокусе, если есть результаты
                        if (
                            suggestions.length > 0 &&
                            debouncedValue.length >= 2
                        ) {
                            setShowDropdown(true);
                        }
                    }
                }}
                onBlur={onBlur}
                onKeyDown={disabled ? undefined : onKeyDown}
                className={`w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg_white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    disabled ? "opacity-50 cursor-not-allowed" : ""
                } ${className}`}
                placeholder={placeholder}
                autoComplete="off"
                disabled={disabled}
            />

            {(isLoading || isFetching) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
            )}

            {showDropdown && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((user) => (
                        <div
                            key={user.id}
                            onClick={() => handleUserClick(user)}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {formatUserDisplay(user)}
                                    </div>
                                    {user.secret_cookie && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            Cookie: {user.secret_cookie}
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-blue-600 dark:text-blue-400">
                                    #{user.id}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showDropdown &&
                !isLoading &&
                !isFetching &&
                debouncedValue.length >= 2 &&
                suggestions.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                        Пользователи не найдены
                    </div>
                )}
        </div>
    );
};
